<template>
  <div class="wrapper">
    <h1>Available Tasks</h1>
    <template>
      <!-- content -->
      <div v-if="isLoading" class="loading" data-cy="spinner"></div>

      <template v-if="tasks">
        <div v-if="tasks.length > 0">
          <div class="table table--8-columns">
            <div class="table__headers">
              <div class="table__header">Prio</div>
              <div class="table__header">Created date</div>
              <div class="table__header">Type</div>
              <div class="table__header">Sites</div>
              <div class="table__header">Brand/Keyword</div>
              <div class="table__header">Wordcount</div>
              <div class="table__header">Deadline</div>
              <div class="table__header">Bonus</div>
            </div>
            <template v-for="(task, index) in tasks">
              <div :key="task.id" :data-cy="`task-${index}`" class="table__row" @click="toggleOpen($event)">
                <div class="table__cell">
                  <!-- prettier-ignore -->
                  <svg v-if="task.isPriority" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="#de7d7d" width="14px" height="14px"><path d="M0 0h24v24H0z" fill="none"/><circle cx="12" cy="19" r="2"/><path d="M10 3h4v12h-4z"/></svg>
                </div>
                <div class="table__cell">
                  {{ task.createdAt | formatDate }}
                </div>
                <div class="table__cell">{{ task.type }}</div>
                <div class="table__cell">{{ formattedSites(task.content.siteIds) }}</div>
                <div v-if="task.content.brandId" class="table__cell">{{ task.content.brandId }}</div>
                <div v-else class="table__cell">{{ task.content.keyword }}</div>
                <div class="table__cell">{{ task.wordCountMin }} - {{ task.wordCountMax }}</div>

                <div class="table__cell">
                  {{ taskDeadline(task) | formatDate }}
                  <!-- prettier-ignore -->
                  <svg v-if="taskDeadline(task)" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="#de7d7d" width="12px" height="12px"><path d="M0 0h24v24H0z" fill="none"/><circle cx="12" cy="19" r="2"/><path d="M10 3h4v12h-4z"/></svg>
                </div>
                <div class="table__cell">{{ task.bonusPrice }} €</div>
                <!-- prettier-ignore -->
                <svg class="arrow-left" xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24"><path d="M16.59 8.59L12 13.17 7.41 8.59 6 10l6 6 6-6z" fill="#999"></path></svg>
              </div>
              <div :key="`${task.id}-foldout`" class="table__foldout">
                <div class="foldout__description">
                  <span>Task Description</span>
                  <p>{{ task.notesFreelancers }}</p>
                </div>
                <button @click="claimTask(task.id)" class="button button--thin">Claim task</button>
              </div>
            </template>
          </div>
        </div>
      </template>

      <div v-else>
        <span>No tasks available in your language</span>
      </div>

      <div class="link" v-if="!languageSelected">
        <router-link to="/profile">Please select a language in your profile settings</router-link>
        <!-- prettier-ignore -->
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="black" width="18px" height="18px"><path d="M0 0h24v24H0V0z" fill="none"/><path d="M8.59 16.59L13.17 12 8.59 7.41 10 6l6 6-6 6-1.41-1.41z"/></svg>
      </div>
    </template>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import moment from 'moment'

export default {
  name: 'overview',
  data() {
    return {
      isLoading: true,
      languageSelected: true,
    }
  },
  mounted: function() {
    if (!this.user.data.firestore.languages) {
      this.languageSelected = false
      this.isLoading = false
    } else {
      this.getTasks()
    }
  },
  methods: {
    ...mapActions(['fetchTasks', 'claimTaskById', 'saveTask']),

    async claimTask(id) {
      this.claimTaskById({ taskId: id })
      this.$router.push({ name: 'taskId', params: { id } })
    },
    async getTasks() {
      const freelancerLanguages = [this.user.data.firestore.languages, ...this.user.data.firestore.secondaryLanguages]
      const languageMarkets = [...new Set(freelancerLanguages.map(language => this.marketsByLanguageID(language)).flat())]

      await this.fetchTasks({
        params: {
          endpoint: 'getbyfilters',
          markets: languageMarkets,
          taskTypes: this.user.data.firestore.taskTypes,
          unclaimed: 'yes',
        },
        userId: this.user.data.uid,
      })
      this.isLoading = false
    },
    formattedSites(sites) {
      return sites.join()
    },
    toggleOpen($event) {
      $event.target.classList.toggle('open')
    },
    submitDateFormatted(submitDate) {
      let date = new Date(submitDate)
      return date.toLocaleString('da-DK')
    },
  },
  computed: {
    ...mapGetters(['tasks', 'user', 'marketsByLanguageID', 'taskDeadline']),
  },
  filters: {
    formatDate: function(date) {
      if (!date || date === '0001-01-01T00:00:00.000Z' || date === '0001-01-01T00:00:00Z') return '-'
      return moment(date).format('DD-MM-YYYY')
    },
  },
}
</script>
<style lang="scss" scoped>
@import '@/assets/shared-styles/button.scss';
@import '@/assets/shared-styles/table.scss';
@import '@/assets/shared-styles/loader.scss';
.wrapper {
  display: grid;
  gap: 1.5rem;
  align-items: start;
  padding: 2rem 2rem;
}

.table--8-columns {
  .table__headers,
  .table__row {
    grid-template-columns: 50px minmax(90px, 130px) 120px 140px 130px 110px 150px minmax(100px, 800px) 30px;
  }
  .arrow-left {
    transform: rotate(90deg);
  }
}
.table__foldout {
  grid-template-columns: 1fr auto;
  span {
    color: #a3a6b4;
    font-weight: 700;
    font-size: 0.6875rem;
    text-transform: uppercase;
    margin-bottom: 0.5rem;
  }
  p {
    font-size: 0.8125rem;
    margin: 0;
  }
}
button {
  height: fit-content;
  align-self: center;
}

.foldout__description {
  display: flex;
  flex-direction: column;
}

.link {
  display: flex;
  justify-content: center;
  align-items: center;
  > a {
    text-decoration: none;
    color: #43425d;
  }
  > a:hover {
    color: var(--color-state);
    + svg {
      fill: var(--color-state);
    }
  }
  > svg {
    margin-left: 0.5rem;
  }
}
</style>
